import { getSearchTermsFromSearchValue } from 'lib/riskbase';
import { compare } from 'natural-orderby';
import {
  AbridgedAsset,
  AssetsFilter,
  AssetsOrder,
  AssetsSort,
  AssetsSortOrder,
} from 'store/assets/types';
import { OrganisationListItem } from 'store/organisations/types';

const SEPARATOR = '$';
const ID_PREFIX = 'rb-';

const getSoonestAssessmentDue = (
  asset: AbridgedAsset,
  organisation?: OrganisationListItem,
): number =>
  Object.entries(asset.compliance_summary).reduce(
    (acc, [complianceSubtypeId, item]) => {
      const due =
        item.due_date &&
        (item.assigned || item.status_id !== 'notstarted') &&
        organisation?.compliance_subtypes[complianceSubtypeId]
          ? new Date(item.due_date).getTime()
          : Infinity;

      return due < acc ? due : acc;
    },
    Infinity,
  );

const sortByAssessmentDueDate = (
  a: AbridgedAsset,
  b: AbridgedAsset,
  organisations: Record<string, OrganisationListItem>,
): number => {
  const [prev, next] = [a, b].map((asset) =>
    getSoonestAssessmentDue(asset, organisations[asset.organisation_id]),
  );
  return prev! < next!
    ? -1
    : prev! > next!
      ? 1
      : compare()(a.details.name, b.details.name);
};

const filterAsset =
  (filters: AssetsFilter) =>
  (asset: AbridgedAsset): boolean => {
    const details = Object.values(asset.details)
      .concat([ID_PREFIX + asset.reference])
      .join(SEPARATOR)
      .toLowerCase();
    const statuses = Object.values(asset.compliance_summary).map(
      (summary) => summary.status_id,
    );
    if (
      filters.organisations.length > 0 &&
      !filters.organisations.includes(asset.organisation_id)
    ) {
      return false;
    }
    if (
      filters.assessment_statuses.length > 0 &&
      !filters.assessment_statuses.some((status) => statuses.includes(status))
    ) {
      return false;
    }
    if (
      getSearchTermsFromSearchValue(filters.search).some(
        (term) => !details.includes(term),
      )
    ) {
      return false;
    }
    return true;
  };

export const filterAndSortAssets = (
  assets: AbridgedAsset[],
  organisations: Record<string, OrganisationListItem>,
  filters: AssetsFilter,
  sortOrder: AssetsSortOrder,
): AbridgedAsset[] => {
  const [sort, order] = sortOrder.split(':') as [AssetsSort, AssetsOrder];
  const list = assets
    .filter(filterAsset(filters))
    .sort((a, b) =>
      sort === 'name'
        ? compare()(a.details.name, b.details.name)
        : sortByAssessmentDueDate(a, b, organisations),
    );
  return order === 'desc' ? list.reverse() : list;
};
